import { inject, Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import { UsuariosService } from './usuarios.service';

export interface AppNotification {
  messageId: string;
  type: string;
  title: string;
  description: string;
  sentAt: string;
  metadata?: Record<string, any>;
  read: boolean;
  expiresAt: string | null;
}

export interface SendNotificationPayload {
  type: string;
  title: string;
  description: string;
  metadata?: Record<string, any>;
  targetUserIds: string[];
  sentAt?: string;
}

const STORAGE_KEY = 'nexia_notifications';
const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const SENT_TRACKER_KEY = 'nexia_notif_sent_tracker';

@Injectable({ providedIn: 'root' })
export class WebSocketNotificationService implements OnDestroy {
  private readonly WS_URL = environment.API_DOMAINS.URL_WEBSOCKET;
  private readonly NOTIFY_URL = environment.API_DOMAINS.URL_NOTIFICATION_SEND;
  private get NOTIFICATIONS_BASE(): string {
    return this.NOTIFY_URL.replace(/\/notify$/, '');
  }

  private http = inject(HttpClient);
  private usuariosService = inject(UsuariosService);

  private ws: WebSocket | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private destroyed$ = new Subject<void>();
  private currentUserId: string | null = null;
  private currentRole: string | null = null;

  private notificationsSubject = new BehaviorSubject<AppNotification[]>([]);
  readonly notifications$ = this.notificationsSubject.asObservable();

  private unreadCountSubject = new BehaviorSubject<number>(0);
  readonly unreadCount$ = this.unreadCountSubject.asObservable();

  constructor() {
    this.loadFromStorage();
  }

  private loadFromStorage(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const stored: AppNotification[] = JSON.parse(raw);
      const now = Date.now();
      const valid = stored.filter(n => {
        if (n.read && n.expiresAt) {
          return new Date(n.expiresAt).getTime() > now;
        }
        return true;
      });
      this.notificationsSubject.next(valid);
      this.unreadCountSubject.next(valid.filter(n => !n.read).length);
    } catch {
      // ignorar storage corrupto
    }
  }

  private saveToStorage(notifications: AppNotification[]): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(notifications));
    } catch {
      // ignorar errores de storage
    }
  }

  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  }

  /**
   * Normaliza el nombre descriptivo del perfil al rol canónico esperado por el backend.
   * Ejemplos: "Director - Consultora" → "DIRECTOR", "Consultor - Desarrollo" → "CONSULTOR"
   */
  static normalizeRole(nombre: string): string {
    const n = nombre.toUpperCase();
    if (n.includes('FINANZAS'))       return 'FINANZAS';
    if (n.includes('DESARROLLO'))     return 'DESARROLLO';
    if (n.includes('CONSULTOR'))      return 'CONSULTOR';
    if (n.includes('DIRECTOR'))       return 'DIRECTOR';
    if (n.includes('ADMINISTRADOR'))  return 'ADMINISTRADOR';
    return n;
  }

  connect(userId: string, role?: string): void {
    this.currentUserId = userId;
    this.currentRole = role ?? null;

    if (this.ws && this.ws.readyState === WebSocket.OPEN) return;

    let url = `${this.WS_URL}?userId=${encodeURIComponent(userId)}`;
    if (role) {
      url += `&role=${encodeURIComponent(role)}`;
    }
    this.ws = new WebSocket(url);

    this.ws.onopen = () => {
      if (userId) {
        this.getNotifications(userId).catch(() => {});
      }
    };

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        // Handle incoming WS notification (new format)
        const notification: AppNotification = {
          messageId: data.messageId ?? this.generateId(),
          type: data.type ?? 'INFO',
          title: data.title ?? 'Notificación',
          description: data.description ?? data.message ?? '',
          sentAt: data.sentAt ?? new Date().toISOString(),
          metadata: data.metadata,
          read: false,
          expiresAt: null,
        };
        const current = this.notificationsSubject.getValue();
        // Avoid duplicates
        if (current.some(n => n.messageId === notification.messageId)) return;
        const updated = [notification, ...current];
        this.notificationsSubject.next(updated);
        this.unreadCountSubject.next(updated.filter(n => !n.read).length);
        this.saveToStorage(updated);
      } catch {
        // mensaje no JSON, ignorar
      }
    };

    this.ws.onclose = () => {
      this.scheduleReconnect(userId, role);
    };

    this.ws.onerror = () => {
      this.ws?.close();
    };
  }

  private scheduleReconnect(userId: string, role?: string): void {
    if (this.reconnectTimer) return;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect(userId, role ?? undefined);
    }, 5000);
  }

  /**
   * Obtiene las notificaciones pendientes del servidor para un usuario.
   */
  async getNotifications(userId: string): Promise<void> {
    try {
      const data = await this.http
        .get<AppNotification[]>(`${this.NOTIFICATIONS_BASE}/notifications`, {
          params: { userId }
        })
        .toPromise();
      if (!Array.isArray(data)) return;
      const current = this.notificationsSubject.getValue();
      const currentIds = new Set(current.map(n => n.messageId));
      const incoming = data.map((n: any) => ({
        messageId: n.messageId ?? n.id ?? this.generateId(),
        type: n.type ?? 'INFO',
        title: n.title ?? 'Notificación',
        description: n.description ?? n.message ?? '',
        sentAt: n.sentAt ?? n.at ?? new Date().toISOString(),
        metadata: n.metadata,
        read: n.read ?? false,
        expiresAt: n.expiresAt ?? null,
      } as AppNotification));
      const newOnes = incoming.filter(n => !currentIds.has(n.messageId));
      if (newOnes.length === 0) return;
      const merged = [...newOnes, ...current];
      this.notificationsSubject.next(merged);
      this.unreadCountSubject.next(merged.filter(n => !n.read).length);
      this.saveToStorage(merged);
    } catch {
      // Si falla el GET, continúa con las notificaciones en storage
    }
  }

  /**
   * Envía una notificación. Acepta roles (strings) que se resuelven a userIds.
   * @param title Título de la notificación
   * @param description Descripción / cuerpo
   * @param recipients Roles destinatarios (e.g. ['CONSULTOR', 'DESARROLLO'])
   * @param metadata Metadatos opcionales (hitoTipo, proyectoId, etc.)
   * @param type Tipo de notificación (por defecto 'INFO')
   */
  async sendNotification(
    title: string,
    description: string,
    recipients?: string[],
    metadata?: Record<string, any>,
    type: string = 'INFO'
  ): Promise<any> {
    const targetUserIds = recipients?.length
      ? await this.resolveUserIdsByRoles(recipients)
      : [];

    const payload: SendNotificationPayload = {
      type,
      title,
      description,
      targetUserIds,
      sentAt: new Date().toISOString(),
      ...(metadata ? { metadata } : {}),
    };

    return this.http
      .post(this.NOTIFY_URL, payload, {
        headers: { 'Content-Type': 'application/json' }
      })
      .toPromise();
  }

  /**
   * Envía una notificación directamente a userIds específicos (sin resolver roles).
   */
  sendNotificationToUsers(payload: SendNotificationPayload): Promise<any> {
    const body: SendNotificationPayload = {
      ...payload,
      sentAt: payload.sentAt ?? new Date().toISOString(),
    };
    return this.http
      .post(this.NOTIFY_URL, body, {
        headers: { 'Content-Type': 'application/json' }
      })
      .toPromise();
  }

  /**
   * Resuelve roles a userIds consultando la API de usuarios.
   */
  private async resolveUserIdsByRoles(roles: string[]): Promise<string[]> {
    try {
      const response: any = await this.usuariosService.listarUsuarios({ limit: 1000 } as any);
      const usuarios: any[] = Array.isArray(response) ? response : (response?.rows ?? []);
      const rolesUpper = roles.map(r => r.toUpperCase());
      return usuarios
        .filter((u: any) => {
          const rawPerfil = (u.perfil?.nombre ?? u.perfil?.rolNombre ?? u.role ?? u.rol ?? '');
          const perfil = rawPerfil ? WebSocketNotificationService.normalizeRole(rawPerfil) : '';
          return rolesUpper.includes(perfil);
        })
        .map((u: any) => String(u.usuId ?? u.id ?? ''))
        .filter(id => !!id);
    } catch {
      return [];
    }
  }

  /**
   * Marca una notificación como leída vía PATCH y actualiza localmente.
   */
  async markAsRead(messageId: string, userId?: string): Promise<void> {
    const resolvedUserId = userId ?? this.currentUserId ?? '';
    try {
      await this.http
        .patch(`${this.NOTIFICATIONS_BASE}/notifications/read`, {
          messageId,
          userId: resolvedUserId,
          readAt: new Date().toISOString(),
          ttl: Math.floor((Date.now() + ONE_WEEK_MS) / 1000),
        })
        .toPromise();
    } catch {
      // Si falla el PATCH, igual actualizamos localmente
    }

    const current = this.notificationsSubject.getValue();
    const updated = current.map(n => {
      if (n.messageId === messageId && !n.read) {
        return {
          ...n,
          read: true,
          expiresAt: new Date(Date.now() + ONE_WEEK_MS).toISOString(),
        };
      }
      return n;
    });
    this.notificationsSubject.next(updated);
    this.unreadCountSubject.next(updated.filter(n => !n.read).length);
    this.saveToStorage(updated);
  }

  // ─── Timed-notification helpers ─────────────────────────────────────────────

  private getSentTracker(): Record<string, string> {
    try {
      const raw = localStorage.getItem(SENT_TRACKER_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  }

  private saveSentTracker(tracker: Record<string, string>): void {
    try { localStorage.setItem(SENT_TRACKER_KEY, JSON.stringify(tracker)); } catch {}
  }

  private wasNotifSentEver(key: string): boolean {
    return !!this.getSentTracker()[key];
  }

  private wasNotifSentThisWeek(key: string): boolean {
    const ts = this.getSentTracker()[key];
    if (!ts) return false;
    return Date.now() - new Date(ts).getTime() < ONE_WEEK_MS;
  }

  private markNotifSent(key: string): void {
    const tracker = this.getSentTracker();
    tracker[key] = new Date().toISOString();
    this.saveSentTracker(tracker);
  }

  /**
   * Verifica proyectos próximos a vencer (dentro de 1 semana) y envía la
   * notificación "Proyecto por Finalizar" una sola vez por proyecto.
   */
  checkProyectosPorFinalizar(proyectos: Array<{ proyId: number | string; nombreProyecto: string; fechaFin: string; estado: string }>): void {
    const now = Date.now();
    proyectos.forEach(p => {
      if (!p.fechaFin) return;
      const estado = (p.estado ?? '').toLowerCase();
      if (estado === 'finalizado' || estado === 'cancelado' || estado === 'cerrado') return;
      const fin = new Date(p.fechaFin).getTime();
      if (isNaN(fin)) return;
      const diff = fin - now;
      if (diff > 0 && diff <= ONE_WEEK_MS) {
        const key = `por_finalizar_${p.proyId}`;
        if (!this.wasNotifSentEver(key)) {
          this.sendNotification('Proyecto por Finalizar', `El proyecto ${p.nombreProyecto} está por finalizar.`, ['CONSULTOR', 'DESARROLLO']).catch(() => {});
          this.markNotifSent(key);
        }
      }
    });
  }

  /**
   * Verifica hitos de ingreso y pago por vencer / vencidos y envía las
   * notificaciones correspondientes con deduplicación por localStorage.
   */
  checkHitosPorVencer(
    hitosIngreso: any[],
    hitosPago: any[],
    nombreProyecto: string,
    proyId: string | number
  ): void {
    const now = Date.now();

    hitosIngreso.forEach((h, idx) => {
      if (!h.fechaPactada) return;
      if (Number(h.gesfiningresoestId) === 1) return;
      const fecha = new Date(h.fechaPactada).getTime();
      if (isNaN(fecha)) return;
      const diff = fecha - now;
      const uid = h.nombre ? `${proyId}_i_${h.nombre}` : `${proyId}_i_${idx}`;

      if (diff > 0 && diff <= ONE_WEEK_MS) {
        const key = `hito_pv_${uid}`;
        if (!this.wasNotifSentEver(key)) {
          this.sendNotification('Hito por vencer', `El Hito ${h.nombre} del proyecto ${nombreProyecto} está por vencer.`, ['CONSULTOR', 'DESARROLLO']).catch(() => {});
          this.markNotifSent(key);
        }
      } else if (diff < 0) {
        const daysOverdue = Math.abs(diff) / (24 * 60 * 60 * 1000);
        if (daysOverdue < 7) {
          const key = `hito_venc_${uid}`;
          if (!this.wasNotifSentEver(key)) {
            this.sendNotification('Hito vencido', `El Hito ${h.nombre} del proyecto ${nombreProyecto} ha vencido la fecha del proyecto se ha extendido.`, ['CONSULTOR', 'DESARROLLO']).catch(() => {});
            this.markNotifSent(key);
          }
        } else {
          const key = `hito_venc1s_${uid}`;
          if (!this.wasNotifSentThisWeek(key)) {
            this.sendNotification('Hito vencido hace 1 semana', `El Hito ${h.nombre} del proyecto ${nombreProyecto} ha vencido hace 1 semana.`, ['CONSULTOR', 'DESARROLLO']).catch(() => {});
            this.markNotifSent(key);
          }
        }
      }
    });

    hitosPago.forEach((h, idx) => {
      if (!h.fechaPactada) return;
      if (Number(h.gesfinpagoestId) === 1) return;
      const fecha = new Date(h.fechaPactada).getTime();
      if (isNaN(fecha)) return;
      const diff = fecha - now;
      const nombre = h.descripcion ?? h.nombre ?? '';
      const uid = nombre ? `${proyId}_p_${nombre}` : `${proyId}_p_${idx}`;

      if (diff > 0 && diff <= ONE_WEEK_MS) {
        const key = `hito_pv_${uid}`;
        if (!this.wasNotifSentEver(key)) {
          this.sendNotification('Hito por vencer', `El Hito ${nombre} del proyecto ${nombreProyecto} está por vencer.`, ['CONSULTOR', 'DESARROLLO']).catch(() => {});
          this.markNotifSent(key);
        }
      } else if (diff < 0) {
        const daysOverdue = Math.abs(diff) / (24 * 60 * 60 * 1000);
        if (daysOverdue < 7) {
          const key = `hito_venc_${uid}`;
          if (!this.wasNotifSentEver(key)) {
            this.sendNotification('Hito vencido', `El Hito ${nombre} del proyecto ${nombreProyecto} ha vencido la fecha del proyecto se ha extendido.`, ['CONSULTOR', 'DESARROLLO']).catch(() => {});
            this.markNotifSent(key);
          }
        } else {
          const key = `hito_venc1s_${uid}`;
          if (!this.wasNotifSentThisWeek(key)) {
            this.sendNotification('Hito vencido hace 1 semana', `El Hito ${nombre} del proyecto ${nombreProyecto} ha vencido hace 1 semana.`, ['CONSULTOR', 'DESARROLLO']).catch(() => {});
            this.markNotifSent(key);
          }
        }
      }
    });
  }

  disconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.ws?.close();
    this.ws = null;
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
    this.disconnect();
  }
}
